/**
 * ActiveBehavioralCampaigns — Admin → Strategy
 * ------------------------------------------------------------------
 * Operations console showing all live campaigns with progression,
 * projected virality, replay velocity, retention curve, conditioning
 * score, and fatigue risk.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Activity, Radio, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  CAMPAIGN_CREATED_EVENT,
  CAMPAIGN_UPDATED_EVENT,
  fetchCampaigns,
  fetchCampaignSequences,
  ROLE_LABEL,
  type StrategicRole,
} from "@/lib/campaignEngine";

interface CampaignRow {
  id: string;
  campaign_name: string;
  dominant_platform: string | null;
  dominant_archetype: string | null;
  projected_virality: number;
  projected_retention: number;
  audience_conditioning_score: number;
  fatigue_risk: number;
  status: string;
  created_at: string;
  rationale: string | null;
  sequences?: any[];
}

const roleDot: Record<StrategicRole, string> = {
  hook_ambiguity: "bg-cyan",
  behavioral_reveal: "bg-amber-400",
  escalation_confrontation: "bg-red-500",
  pov_reframing: "bg-violet-400",
  replay_reinforcement: "bg-emerald-400",
};

export default function ActiveBehavioralCampaigns() {
  const [items, setItems] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const camps = (await fetchCampaigns()) as CampaignRow[];
    const enriched = await Promise.all(
      camps.map(async (c) => ({ ...c, sequences: await fetchCampaignSequences(c.id) })),
    );
    setItems(enriched);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener(CAMPAIGN_CREATED_EVENT, refresh);
    window.addEventListener(CAMPAIGN_UPDATED_EVENT, refresh);
    const ch = (supabase as any)
      .channel("active-campaigns")
      .on("postgres_changes", { event: "*", schema: "public", table: "behavioral_campaigns" }, refresh)
      .subscribe();
    return () => {
      window.removeEventListener(CAMPAIGN_CREATED_EVENT, refresh);
      window.removeEventListener(CAMPAIGN_UPDATED_EVENT, refresh);
      (supabase as any).removeChannel(ch);
    };
  }, []);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Radio className="w-5 h-5 text-cyan animate-pulse" />
          Active Behavioral Campaigns
          <Badge variant="outline" className="ml-2 text-[10px] border-cyan/40 text-cyan">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <div className="text-xs text-muted-foreground">Scanning operating system...</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-muted-foreground italic">No campaigns deployed yet — assemble one in the Builder above.</div>
        )}
        {items.map((c) => {
          const reps = c.sequences ?? [];
          const replayVel = Math.round(reps.reduce((s, r) => s + Number(r.projected_replay_impact || 0), 0) / Math.max(1, reps.length));
          const retCurve = reps.map((r) => Number(r.projected_retention_impact || 0));
          return (
            <div key={c.id} className="rounded border border-glass-border bg-charcoal-deep/60 p-3 space-y-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-foreground font-medium truncate">{c.campaign_name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                    {c.dominant_platform ?? "multi"} · {c.dominant_archetype ?? "—"} · {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <Badge className={`text-[10px] ${c.status === "active" ? "bg-cyan/15 text-cyan border-cyan/40" : "bg-muted/30 text-muted-foreground"}`}>
                  {c.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
                <Metric label="Virality" value={c.projected_virality} tone="cyan" />
                <Metric label="Retention" value={c.projected_retention} tone="emerald" />
                <Metric label="Conditioning" value={c.audience_conditioning_score} tone="violet" />
                <Metric label="Fatigue" value={c.fatigue_risk} tone={c.fatigue_risk > 60 ? "red" : "amber"} />
              </div>

              {/* Escalation map */}
              <div className="flex items-center gap-1">
                {reps.map((r, idx) => (
                  <div key={r.id} className="flex-1 group relative">
                    <div className={`h-1.5 rounded-full ${roleDot[r.strategic_role as StrategicRole] ?? "bg-muted"} opacity-80`} />
                    <div className="text-[9px] text-muted-foreground mt-1 truncate font-mono">
                      #{idx + 1} · {ROLE_LABEL[r.strategic_role as StrategicRole] ?? r.strategic_role}
                    </div>
                  </div>
                ))}
              </div>

              {/* Retention curve sparkline */}
              <div className="flex items-end gap-0.5 h-8">
                {retCurve.map((v, i) => (
                  <div key={i} className="flex-1 bg-emerald-400/60 rounded-t-sm" style={{ height: `${Math.max(8, v)}%` }} title={`${v}%`} />
                ))}
              </div>

              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Activity className="w-3 h-3 text-cyan" />
                  Replay velocity <span className="text-cyan font-mono ml-1">{replayVel}%</span>
                </div>
                {c.fatigue_risk > 60 && (
                  <div className="flex items-center gap-1 text-amber-300">
                    <AlertTriangle className="w-3 h-3" /> fatigue elevated
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone: "cyan" | "emerald" | "violet" | "amber" | "red" }) {
  const t = {
    cyan: "text-cyan", emerald: "text-emerald-300", violet: "text-violet-300", amber: "text-amber-300", red: "text-red-400",
  }[tone];
  return (
    <div className="rounded bg-charcoal-medium/60 border border-glass-border px-2 py-1.5">
      <div className="uppercase tracking-wider text-muted-foreground text-[9px]">{label}</div>
      <div className={`font-mono text-sm ${t}`}>{value}%</div>
      <Progress value={value} className="h-0.5 mt-1" />
    </div>
  );
}
